import gsap from "@lib/gsap";
import { CustomEase } from "gsap/CustomEase";
import { onMount, onDestroy } from "@/modules/_";

gsap.registerPlugin(CustomEase);
CustomEase.create("slider-wipe", "0.6, 0.08, 0.02, 0.99");

const DURATION = 0.9;
const PANEL_CONTENT = ".rich-text-component, .plain-text-component";

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const panels = Array.from(element.querySelectorAll<HTMLElement>(".stats-slider_panel"));
  const images = Array.from(element.querySelectorAll<HTMLElement>(".stats-slider_image"));
  const imgs = Array.from(element.querySelectorAll<HTMLElement>(".stats-slider_image img"));
  const controls = Array.from(element.querySelectorAll<HTMLElement>(".stats-slider_pill"));

  // Read initial active index from controls, fallback to active panel, then 0.
  let current = controls.findIndex((control) => control.classList.contains("is-active"));
  if (current === -1) current = panels.findIndex((panel) => panel.classList.contains("is-active"));
  if (current === -1 || current >= panels.length || current >= images.length) current = 0;

  let animating = false;

  function getChildren(panel: HTMLElement) {
    return Array.from(panel.querySelectorAll<HTMLElement>(PANEL_CONTENT));
  }

  function initState() {
    images.forEach((img, i) => {
      // Clear Webflow-generated clip-path inline styles, then position off-screen
      gsap.set(imgs[i], { clipPath: "none" });
      gsap.set(img, { xPercent: i === current ? 0 : 100 });
    });

    panels.forEach((panel, i) => {
      const children = getChildren(panel);
      // Disable CSS transitions on children so GSAP has exclusive control.
      // This prevents conflicts with both Webflow inline styles and CSS transitions.
      children.forEach((child) => {
        child.style.transition = "none";
      });

      if (i === current) {
        panel.classList.add("is-active");
        gsap.set(panel, { opacity: 1, "--panel-line-scale": 1 });
        gsap.set(children, { opacity: 1, y: 0 });
      } else {
        panel.classList.remove("is-active");
        gsap.set(panel, { opacity: 0, "--panel-line-scale": 0 });
        gsap.set(children, { opacity: 0, y: 12 });
      }
    });

    images.forEach((img, i) => img.classList.toggle("is-active", i === current));
    controls.forEach((control, i) => control.classList.toggle("is-active", i === current));
  }

  function goTo(index: number) {
    if (index === current || animating || index < 0 || index >= panels.length || index >= images.length) {
      return;
    }
    animating = true;

    const direction = index > current ? 1 : -1;
    const prevImg = images[current];
    const nextImg = images[index];
    const prevImgInner = imgs[current];
    const nextImgInner = imgs[index];
    const prevPanel = panels[current];
    const nextPanel = panels[index];
    const nextChildren = getChildren(nextPanel);

    // Stage the incoming image just off-screen in the correct direction
    gsap.set(nextImg, { xPercent: direction * 100 });
    gsap.set(nextImgInner, { xPercent: -direction * 30 });

    controls[current]?.classList.remove("is-active");
    controls[index]?.classList.add("is-active");

    const prevIndex = current;

    gsap
      .timeline({
        defaults: { duration: DURATION, ease: "slider-wipe" },
        onComplete() {
          images[prevIndex].classList.remove("is-active");
          prevPanel.classList.remove("is-active");
          current = index;
          animating = false;
        },
      })
      // Slide current image out with parallax
      .to(prevImg, { xPercent: -direction * 100 }, 0)
      .to(prevImgInner, { xPercent: direction * 30 }, 0)
      // Slide next image in with parallax
      .to(nextImg, { xPercent: 0 }, 0)
      .to(nextImgInner, { xPercent: 0 }, 0)
      // Fade out current text panel
      .to(prevPanel, { opacity: 0, duration: 0.25 }, 0)
      .to(prevPanel, { "--panel-line-scale": 0, duration: 0.3, ease: "power2.out" }, 0)
      // Show next text panel and stagger in its children
      .call(
        () => {
          nextImg.classList.add("is-active");
          nextPanel.classList.add("is-active");
          gsap.set(nextPanel, { opacity: 1 });
        },
        undefined,
        0.2
      )
      .to(nextPanel, { "--panel-line-scale": 1, duration: 0.55, ease: "power2.out" }, 0.2)
      .fromTo(
        nextChildren,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.08, ease: "power2.out" },
        0.25
      );
  }

  function handleControlClick(this: HTMLElement) {
    goTo(Number(this.dataset.index));
  }

  onMount(() => {
    initState();
    controls.forEach((control) => control.addEventListener("click", handleControlClick));
  });

  onDestroy(() => {
    controls.forEach((control) => control.removeEventListener("click", handleControlClick));
    gsap.killTweensOf([...images, ...imgs, ...panels]);
  });
}
